import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import {
  FaSearch,
  FaEye,
  FaTimes,
  FaCheck,
  FaTruck,
  FaTrash,
  FaStore,
  FaGlobeAmericas,
  FaMapMarkerAlt,
} from "react-icons/fa";
import styles from "./AdminOrdersPage.module.css";

function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("hider_access_token");
    if (!token) {
      navigate("/hider-login");
    }
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const token = sessionStorage.getItem("hider_access_token");

      // Verify URL matches server.js config (usually /api/orders)
      const res = await fetch("/api/orders", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`Error: ${res.status}`);

      const data = await res.json();
      if (Array.isArray(data)) {
        setOrders(data);
        setFilteredOrders(data);
      } else {
        setOrders([]);
        setFilteredOrders([]);
      }
    } catch (error) {
      console.error("Error loading orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const totalRevenue = orders.reduce(
    (sum, o) => (o.isPaid ? sum + o.totalPrice : sum),
    0
  );
  const totalOrders = orders.length;
  const pendingDelivery = orders.filter(
    (o) => o.isPaid && !o.isDelivered
  ).length;
  const unpaidOrders = orders.filter((o) => !o.isPaid).length;

  useEffect(() => {
    let result = orders;
    if (activeFilter === "Pending Pay") {
      result = result.filter((o) => !o.isPaid);
    } else if (activeFilter === "To Ship") {
      result = result.filter((o) => o.isPaid && !o.isDelivered);
    } else if (activeFilter === "Shipped") {
      result = result.filter((o) => o.isDelivered);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (o) =>
          o._id.toLowerCase().includes(q) ||
          o.customer.firstName.toLowerCase().includes(q) ||
          o.customer.phone.includes(q)
      );
    }
    setFilteredOrders(result);
  }, [activeFilter, searchQuery, orders]);

  // --- FIXED ACTIONS (Using /api/orders now) ---

  const markAsPaid = async (id) => {
    if (!window.confirm("Confirm manual payment for this order?")) return;
    try {
      const token = sessionStorage.getItem("hider_access_token");
      const res = await fetch(`/api/orders/${id}/pay`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchOrders();
        setSelectedOrder(null);
      } else {
        alert("Failed to mark as paid");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const markAsDelivered = async (id) => {
    if (!window.confirm("Mark order as Shipped/Delivered?")) return;
    try {
      const token = sessionStorage.getItem("hider_access_token");
      const res = await fetch(`/api/orders/${id}/deliver`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchOrders();
        setSelectedOrder(null);
      } else {
        alert("Failed to mark as shipped");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteOrder = async (id) => {
    if (
      !window.confirm("Are you sure you want to PERMANENTLY DELETE this order?")
    )
      return;
    try {
      const token = sessionStorage.getItem("hider_access_token");
      const res = await fetch(`/api/orders/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setOrders(orders.filter((o) => o._id !== id));
        setSelectedOrder(null);
        fetchOrders(); // Refresh to be sure
      } else {
        alert("Failed to delete order");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting order");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("hider_access_token");
    navigate("/");
  };

  const getDeliveryBadge = (method = "") => {
    const m = method.toLowerCase();
    if (
      m.includes("united states") ||
      m.includes("usa") ||
      m.includes("us delivery")
    ) {
      return (
        <span
          style={{
            color: "#a29bfe",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <FaGlobeAmericas /> USA Export
        </span>
      );
    }
    if (m.includes("hawassa")) {
      return (
        <span
          style={{
            color: "#00e5ff",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <FaMapMarkerAlt /> Hawassa
        </span>
      );
    }
    if (m.includes("pickup")) {
      let label = "Store Pickup";
      if (m.includes("bherawi")) label = "Bherawi Pickup";
      if (m.includes("bole")) label = "Bole Pickup";
      if (m.includes("jacros") || m.includes("buncho")) label = "Jacros Pickup";
      return (
        <span
          style={{
            color: "#ff9f43",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <FaStore /> {label}
        </span>
      );
    }
    return (
      <span
        style={{
          color: "#ccc",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <FaTruck /> Addis Delivery
      </span>
    );
  };

  return (
    <div className={styles.adminPage}>
      <Navbar />
      <div className={styles.adminHero}>
        <h1>Sales & Logistics</h1>
        <button onClick={handleLogout} className={styles.logoutBtn}>
          Secure Logout
        </button>
      </div>

      <div className={styles.adminContentContainer}>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Total Revenue</span>
            <span className={`${styles.statValue} ${styles.money}`}>
              {totalRevenue.toLocaleString()} Br
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Total Orders</span>
            <span className={`${styles.statValue} ${styles.orders}`}>
              {totalOrders}
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Ready to Ship</span>
            <span className={`${styles.statValue} ${styles.pending}`}>
              {pendingDelivery}
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Unpaid / Pending</span>
            <span className={`${styles.statValue} ${styles.unpaid}`}>
              {unpaidOrders}
            </span>
          </div>
        </div>

        <div className={styles.controlsWrapper}>
          <div className={styles.filterTabs}>
            {["All", "Pending Pay", "To Ship", "Shipped"].map((status) => (
              <button
                key={status}
                onClick={() => setActiveFilter(status)}
                className={`${styles.filterTab} ${
                  activeFilter === status ? styles.active : ""
                }`}
              >
                {status}
              </button>
            ))}
          </div>
          <div className={styles.searchBox}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search ID, Name, Phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.adminTableContainer}>
          <table className={styles.adminTable}>
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer Details</th>
                <th>Delivery Method</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#888",
                    }}
                  >
                    Loading Data...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#888",
                    }}
                  >
                    No orders matched.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                  const delMethod = order.customer.deliveryMethod || "";
                  return (
                    <tr key={order._id}>
                      <td>
                        <span className={styles.orderId}>
                          #
                          {order._id
                            .substring(order._id.length - 6)
                            .toUpperCase()}
                        </span>
                        <span className={styles.orderDate}>
                          {new Date(order.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td>
                        <span className={styles.customerName}>
                          {order.customer.firstName} {order.customer.lastName}
                        </span>
                        <span className={styles.customerDetail}>
                          {order.customer.phone}
                        </span>
                      </td>
                      <td>
                        {getDeliveryBadge(delMethod)}
                        <span
                          className={styles.customerDetail}
                          style={{
                            marginTop: "5px",
                            paddingLeft: "24px",
                          }}
                        >
                          {order.customer.city || "Addis Ababa"}
                        </span>
                      </td>
                      <td className={styles.totalPrice}>
                        {order.totalPrice.toLocaleString()} Br
                      </td>
                      <td>
                        <div className={styles.statusContainer}>
                          {order.isPaid ? (
                            <span className={`${styles.badge} ${styles.paid}`}>
                              PAID
                            </span>
                          ) : (
                            <span
                              className={`${styles.badge} ${styles.unpaid}`}
                            >
                              UNPAID
                            </span>
                          )}
                          {order.isDelivered ? (
                            <span className={`${styles.badge} ${styles.sent}`}>
                              SENT
                            </span>
                          ) : (
                            <span
                              className={`${styles.badge} ${styles.pending}`}
                            >
                              PROCESSING
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={styles.tableActions}>
                        <div className={styles.actionButtons}>
                          <button
                            className={`${styles.actionBtn} ${styles.viewBtn}`}
                            onClick={() => setSelectedOrder(order)}
                          >
                            <FaEye />
                          </button>
                          {!order.isPaid && (
                            <button
                              className={`${styles.actionBtn} ${styles.payBtn}`}
                              onClick={() => markAsPaid(order._id)}
                            >
                              <FaCheck />
                            </button>
                          )}
                          {order.isPaid && !order.isDelivered && (
                            <button
                              className={`${styles.actionBtn} ${styles.shipBtn}`}
                              onClick={() => markAsDelivered(order._id)}
                            >
                              <FaTruck />
                            </button>
                          )}
                          <button
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => deleteOrder(order._id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedOrder && (
        <div
          className={styles.modalOverlay}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>
                Order #
                {selectedOrder._id
                  .substring(selectedOrder._id.length - 6)
                  .toUpperCase()}
              </h2>
              <FaTimes
                className={styles.closeIcon}
                onClick={() => setSelectedOrder(null)}
              />
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Shipping Info</span>
                <div className={styles.detailValue}>
                  <strong>
                    {selectedOrder.customer.firstName}{" "}
                    {selectedOrder.customer.lastName}
                  </strong>
                  <br />
                  {selectedOrder.customer.address}
                  <br />
                  {selectedOrder.customer.city}
                  <br />
                  Phone: {selectedOrder.customer.phone}
                  <br />
                  Email: {selectedOrder.customer.email}
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "15px",
                      background: "#222",
                      borderRadius: "6px",
                      border: "1px solid #333",
                    }}
                  >
                    <span
                      style={{
                        color: "#888",
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                      }}
                    >
                      Selected Method
                    </span>
                    <div
                      style={{
                        color: "#fff",
                        fontSize: "1.1rem",
                        marginTop: "8px",
                      }}
                    >
                      {getDeliveryBadge(
                        selectedOrder.customer.deliveryMethod || ""
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "#ccc",
                        marginTop: "8px",
                        fontStyle: "italic",
                      }}
                    >
                      {selectedOrder.customer.deliveryMethod}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Order Notes</span>
                <div
                  className={styles.detailValue}
                  style={{
                    background: "#1a1a1a",
                    padding: "12px",
                    borderRadius: "6px",
                    border: "1px solid #333",
                  }}
                >
                  {selectedOrder.customer.notes ? (
                    <span style={{ color: "#e0e0e0" }}>
                      {selectedOrder.customer.notes}
                    </span>
                  ) : (
                    <span style={{ color: "#666", fontStyle: "italic" }}>
                      No notes provided.
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>
                  Items ({selectedOrder.orderItems.length})
                </span>
                <div className={styles.detailValue}>
                  {selectedOrder.orderItems.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                        borderBottom: "1px solid #222",
                        paddingBottom: "8px",
                      }}
                    >
                      <span>
                        <span style={{ color: "#fff", fontWeight: "bold" }}>
                          {item.quantity}x
                        </span>{" "}
                        {item.name}
                      </span>
                      <span style={{ color: "#888" }}>{item.price} Br</span>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className={styles.detailRow}
                style={{ borderBottom: "none", paddingTop: "0" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "1.4rem",
                    color: "#fff",
                    fontWeight: "bold",
                    borderTop: "1px solid #444",
                    paddingTop: "15px",
                  }}
                >
                  <span>Total Paid</span>
                  <span style={{ color: "#00e5ff" }}>
                    {selectedOrder.totalPrice.toLocaleString()} Br
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default AdminOrdersPage;
